import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea'; // Import Textarea
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, EyeOff, Store, Info } from 'lucide-react';
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from 'react-router-dom';
import { useToast } from '@/hooks/toast/index';

// Updated registration form schema
const registerSchema = z.object({
  username: z.string().min(3, { message: "Username must be at least 3 characters" })
    .max(50, { message: "Username must be less than 50 characters" }),
  fullName: z.string().min(2, { message: "Full name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email" }),
  password: z.string()
    .min(8, { message: "Password must be at least 8 characters" })
    .regex(/[A-Z]/, { message: "Password must include at least one uppercase letter" })
    .regex(/[0-9]/, { message: "Password must include at least one number" })
    .regex(/[^A-Za-z0-9]/, { message: "Password must include at least one special character" }),
  confirmPassword: z.string(),
  termsAccepted: z.boolean().refine(val => val === true, {
    message: "You must accept the terms and conditions"
  }),
  privacyAccepted: z.boolean().refine(val => val === true, {
    message: "You must accept the privacy policy"
  }),
  isStore: z.boolean().optional().default(false),
  storeName: z.string().optional(),
  storeDescription: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
}).superRefine((data, ctx) => {
  if (data.isStore) {
    if (!data.storeName || data.storeName.trim().length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Store name must be at least 2 characters",
        path: ["storeName"],
      });
    }
    if (data.storeName && data.storeName.trim().length > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Store name must be less than 100 characters",
        path: ["storeName"],
      });
    }
    if (!data.storeDescription || data.storeDescription.trim().length < 10) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Store description must be at least 10 characters",
        path: ["storeDescription"],
      });
    }
    if (data.storeDescription && data.storeDescription.trim().length > 500) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Store description must be less than 500 characters",
        path: ["storeDescription"],
      });
    }
  }
});

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

interface RegisterMetadata {
  username: string;
  full_name: string;
  location?: string;
  terms_accepted: boolean;
  privacy_accepted: boolean;
  ip_address?: string;
  is_store?: boolean;
  store_name?: string; // Added store_name
  store_description?: string; // Added store_description
}

const RegisterForm = ({ onSwitchToLogin }: RegisterFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register: registerUser } = useAuth();
  const { toast } = useToast();

  const registerForm = useForm<z.infer<typeof registerSchema>>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      username: "",
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      termsAccepted: false,
      privacyAccepted: false,
      isStore: false,
      storeName: "",
      storeDescription: "",
    },
  });

  const watchIsStore = registerForm.watch("isStore");

  const handleRegister = async (values: z.infer<typeof registerSchema>) => {
    try {
      setIsLoading(true);
      let ipAddress;
      try {
        const resp = await fetch('https://api.ipify.org?format=json');
        const data = await resp.json();
        ipAddress = data.ip;
      } catch (err) {
        console.error("Could not get IP address:", err);
      }

      const metadata: RegisterMetadata = {
        username: values.username,
        full_name: values.fullName,
        terms_accepted: values.termsAccepted,
        privacy_accepted: values.privacyAccepted,
        ip_address: ipAddress,
        is_store: values.isStore,
      };

      if (values.isStore) {
        metadata.store_name = values.storeName;
        metadata.store_description = values.storeDescription;
      }

      await registerUser(values.email, values.password, metadata);
      // Registration success is now handled by the new success page
    } catch (error: any) {
      console.error("Registration error:", error);
      if (error.message && (error.message.includes('duplicate key value violates unique constraint "profiles_username_key"') || error.message.toLowerCase().includes('username already exists'))) {
        registerForm.setError("username", {
          type: "manual",
          message: "Ky emër përdoruesi është tashmë i marrë. Ju lutemi zgjidhni një tjetër.",
        });
      } else if (error.message && (error.message.includes('User already registered') || error.message.toLowerCase().includes('email already in use'))) {
        registerForm.setError("email", {
          type: "manual",
          message: "Ky email është tashmë i regjistruar. Provoni të hyni.",
        });
      } else {
        toast({
          title: "Gabim gjatë regjistrimit",
          description: error.message || "Ndodhi një gabim i papritur. Ju lutemi provoni përsëri.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Krijoni një Llogari</CardTitle>
        <CardDescription>
          Regjistrohuni për të blerë dhe shitur produkte, ose për të hapur dyqanin tuaj.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...registerForm}>
          <form onSubmit={registerForm.handleSubmit(handleRegister)} className="space-y-4">
            <FormField
              control={registerForm.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Emri i Përdoruesit</FormLabel>
                  <FormControl>
                    <Input placeholder="emriperdoruesit" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={registerForm.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Emri i Plotë</FormLabel>
                  <FormControl>
                    <Input placeholder="Emri Mbiemri" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={registerForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="juaji@email.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={registerForm.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fjalëkalimi</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input 
                        type={showPassword ? "text" : "password"} 
                        {...field} 
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                  <p className="text-xs text-muted-foreground mt-1">
                    Fjalëkalimi duhet të ketë të paktën 8 karaktere, një shkronjë të madhe, një numër dhe një karakter special.
                  </p>
                </FormItem>
              )}
            />
            
            <FormField
              control={registerForm.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Konfirmoni Fjalëkalimin</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input 
                        type={showConfirmPassword ? "text" : "password"} 
                        {...field} 
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={toggleConfirmPasswordVisibility}
                      >
                        {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={registerForm.control}
              name="isStore"
              render={({ field }) => (
                <FormItem className="flex flex-col space-y-2 rounded-md p-3 border border-gray-200 dark:border-gray-700">
                  <div className="flex flex-row items-start space-x-3">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="isStore"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel htmlFor="isStore" className="text-sm font-normal flex items-center">
                        <Store size={16} className="mr-2 text-brand-500" /> Dua të regjistrohem si Dyqan
                      </FormLabel>
                      <p className="text-xs text-muted-foreground">
                        Zgjidhni këtë opsion nëse doni të listoni produkte si një biznes/dyqan.
                      </p>
                    </div>
                  </div>
                  {field.value && (
                    <div className="mt-2 flex items-start text-xs text-blue-600 bg-blue-50 p-2 rounded-md border border-blue-200">
                      <Info size={14} className="mr-2 mt-0.5 flex-shrink-0" />
                      <span>Aplikimi juaj për dyqan do të shqyrtohet nga një administrator para miratimit.</span>
                    </div>
                  )}
                </FormItem>
              )}
            />
            
            {watchIsStore && (
              <>
                <FormField
                  control={registerForm.control}
                  name="storeName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Emri i Dyqanit</FormLabel>
                      <FormControl>
                        <Input placeholder="Emri i dyqanit tuaj" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={registerForm.control}
                  name="storeDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Përshkrimi i Dyqanit</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Përshkruani shkurtimisht dyqanin tuaj, çfarë produktesh ofroni, etj."
                          {...field}
                          className="min-h-[100px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
            
            <FormField
              control={registerForm.control}
              name="termsAccepted"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md p-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-normal">
                      Unë pranoj <Link to="/terms" className="text-brand-500 hover:underline" target="_blank">Kushtet e Përdorimit</Link>
                    </FormLabel>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={registerForm.control}
              name="privacyAccepted"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md p-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-normal">
                      Unë pranoj <Link to="/privacy" className="text-brand-500 hover:underline" target="_blank">Politikën e Privatësisë</Link>
                    </FormLabel>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
            
            <Button 
              type="submit" 
              className="w-full bg-brand-500 hover:bg-brand-600"
              disabled={isLoading}
            >
              {isLoading ? "Duke u ngarkuar..." : "Regjistrohu"}
            </Button>
          </form>
        </Form>
      </CardContent>
      <CardFooter className="flex flex-col space-y-4">
        <div className="text-sm text-center text-gray-500">
          Keni tashmë një llogari?{" "}
          <Button 
            variant="link" 
            className="p-0 h-auto text-brand-500"
            onClick={onSwitchToLogin}
          >
            Hyni
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default RegisterForm;
